import { InfoPageLayout } from "../../../components/info-page-layout/info-page-layout.component";
import { ConnectionForm } from "../../../domains/connectionForm";
import { ConnectionPreview } from "../../../domains/connectionPreview/connection-preview";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import {TabPanel, TabView} from "primereact/tabview";
import {ProgressBar} from "primereact/progressbar";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAuth} from "../../../providers/auth/auth-provider";
import {useMessage} from "../../../providers/message/message-provider";
import {fetch} from "../../../shared/api/v1";
import type {components} from "../../../shared/api/v1/openapi.types";
import axios from "../../../transport/axios";
import { ConnectionUserFiles } from "../../../domains/connectionUserFiles";
import { Badge } from "primereact/badge";
import { ConnectionComments } from "../../../domains/connectionComments";
import {getConnectionStatusLabel, isConnectionDraft, isConnectionEditable} from '../../../constants/connection';
import {ConnectionExportActions} from '../../../components/connection-export-actions/connection-export-actions';

export const ConnectionPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const {token} = useAuth();
  const {showError, showMessage} = useMessage();
  const [connection, setConnection] = useState<(components['schemas']['Connection'] & { commentsTotal?: number }) | null>(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isDirty, setIsDirty] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const connectionRef = useRef(connection);
  const onConnectionUpdated = useCallback((data: components['schemas']['Connection']) => {
    connectionRef.current = data;
    setConnection(data);
  }, []);
  const [activeIndex, setActiveIndex] = useState(0);
  const title = 'Технологическое присоединение';
  const status = connection?.status ?? null;
  const files = connection?.files ?? [];
  const resultFiles = connection?.resultFiles ?? [];
  const sbitFiles = connection?.sbitFiles ?? [];
  const commentsTotal = connection?.commentsTotal ?? 0;
  const isDraft = isConnectionDraft(status);
  const isEditableStatus = isConnectionEditable(status);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const downloadFile = async (file: components["schemas"]["File"]) => {
    if (!token) {
      showError({ message: "Нет авторизации. Пожалуйста, войдите в аккаунт." });
      return;
    }
    try {
      const response = await axios.get(file.url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.name || `file-${file.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const message = extractErrorMessage(error);
      showError({
        message: message ?? "Ошибка при загрузке файла",
      });
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadConnection = async () => {
      if (isNew || !token || !id) {
        setConnection(null);
        connectionRef.current = null;
        setIsDirty(false);
        setIsLoading(false);
        return;
      }
      if (connectionRef.current?.id === Number(id)) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setIsDirty(false);
      try {
        const response = await fetch.GET('/connections/{id}', {
          params: {
            path: {
              id: Number(id),
            },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!isMounted) {
          return;
        }
        const error = (response as { error?: unknown }).error;
        if (error) {
          const message = extractErrorMessage(error);
          showError({
            message: message ?? 'Ошибка загрузки заявки',
          });
          connectionRef.current = null;
          setConnection(null);
          setIsLoading(false);
          return;
        }
        connectionRef.current = response.data ?? null;
        setConnection(response.data ?? null);
      } catch {
        if (!isMounted) return;
        setConnection(null);
        connectionRef.current = null;
        showError({message: 'Не удалось загрузить заявку. Попробуйте ещё раз.'});
      }
      setIsLoading(false);
    };
    loadConnection();
    return () => {
      isMounted = false;
    };
  }, [id, isNew, showError, token]);

  const showEditTab = isNew || (connection?.id === Number(id) && isEditableStatus);
  const showExtraTabs = !isNew && connection?.id === Number(id);
  const showSubmittedTabs = showExtraTabs && !isDraft;
  const editTabLabel = useMemo(
    () => (isNew ? 'Создание заявления' : 'Редактирование заявления'),
    [isNew],
  );

  const tabOrder = useMemo(
    () => {
      const tabs: Array<{ key: number; aliases: string[] }> = [];
      if (showEditTab) {
        tabs.push({ key: 0, aliases: ['edit', 'manage'] });
      }
      if (showExtraTabs) {
        tabs.push({ key: 1, aliases: ['preview'] });
      }
      if (showSubmittedTabs) {
        tabs.push({ key: 2, aliases: ['files', 'docs'] });
        tabs.push({ key: 3, aliases: ['comments', 'messages'] });
      }
      if (showSubmittedTabs && !isEditableStatus) {
        tabs.push({ key: 4, aliases: ['result-files', 'result'] });
        tabs.push({ key: 5, aliases: ['sbit-files', 'sbit'] });
      }
      return tabs;
    },
    [showEditTab, showExtraTabs, showSubmittedTabs, isEditableStatus],
  );

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (!requestedTab) {
      setActiveIndex(0);
      return;
    }
    const nextIndex = tabOrder.findIndex((tab) => tab.aliases.includes(requestedTab));
    setActiveIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [searchParams, tabOrder, showEditTab]);

  const handleDeleteFiles = async (ids: number[]) => {
    if (!token || !id) {
      showError({ message: "Нет авторизации. Пожалуйста, войдите в аккаунт." });
      return;
    }
    try {
      const response = await fetch.DELETE('/connections/{id}/files', {
        params: {
          path: {
            id: Number(id),
          },
        },
        body: {
          fileIds: ids,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? "Ошибка удаления файлов");
      }
      if (response.data) {
        onConnectionUpdated(response.data);
      }
      showMessage({
        message: "Файлы удалены",
      });
    } catch (error) {
      const message = extractErrorMessage(error);
      showError({
        message: message ?? "Ошибка удаления файлов",
      });
    }
  };

  const noopDeleteFiles = async () => {};

  return (
    <InfoPageLayout title={title}>
      <div className="flex align-items-center gap-2 mb-3">
        <Button
          icon="pi pi-arrow-left"
          text
          aria-label="Назад"
          onClick={() => navigate('/cabinet')}
        />
        <span className="text-lg font-semibold">
          {isNew ? 'Создание заявления' : `Заявление №${id}`}
        </span>
      </div>
      {showExtraTabs ? <div className="mb-3">Статус: {getConnectionStatusLabel(status)}</div> : null}
      <div className="mb-3">
        <ConnectionExportActions id={showExtraTabs ? connection?.id : undefined} disabled={isBusy || isLoading}
          disabledReason={isNew ? 'Сначала сохраните черновик для скачивания' : isDirty
            ? isDraft ? 'Сохраните черновик, чтобы скачать актуальную версию' : 'Сохраните изменения, чтобы скачать актуальную версию'
            : undefined} />
      </div>
      {isLoading ? (
        <div className="mb-2">
          <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
        </div>
      ) : null}
      <TabView
        renderActiveOnly={false}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {showEditTab ? (
          <TabPanel header={editTabLabel} key="edit">
            {!isLoading ? <ConnectionForm key={id} connectionId={id ?? 'new'} connection={isNew ? null : connection}
              onConnectionUpdated={onConnectionUpdated} onDirtyChange={setIsDirty} onBusyChange={setIsBusy} /> : null}
          </TabPanel>
        ) : null}
        {showExtraTabs ? (
          <TabPanel header="Предпросмотр" key="preview">
            {connection ? (
              <ConnectionPreview connection={connection} />
            ) : (
              <div className="text-color-secondary">Нет данных для предпросмотра.</div>
            )}
          </TabPanel>
        ) : null}
        {showSubmittedTabs ? (
          <TabPanel header="Файлы" key="files">
            <ConnectionUserFiles
              files={files}
              onDownload={downloadFile}
              onDeleteSelected={handleDeleteFiles}
              canAdd={true}
              canDelete={isEditableStatus}
              connectionId={id}
              onConnectionUpdated={onConnectionUpdated}
            />
          </TabPanel>
        ) : null}
        {showSubmittedTabs ? (
          <TabPanel
            header={(
              <div style={{ position: 'relative', paddingRight: '1.25rem', display: 'inline-block' }}>
                <span>Сообщения от ТСО</span>
                <Badge
                  value={commentsTotal}
                  severity="danger"
                  style={{ position: 'absolute', top: '-0.35rem', right: '-0.5rem' }}
                />
              </div>
            )}
            key="comments"
          >
            <ConnectionComments connectionId={id} />
          </TabPanel>
        ) : null}
        {showSubmittedTabs && !isEditableStatus ? (
          <TabPanel
            header={(
              <div style={{ position: 'relative', paddingRight: '1.25rem', display: 'inline-block' }}>
                <span>Документы по результатам</span>
                <Badge
                  value={resultFiles.length}
                  severity="danger"
                  style={{ position: 'absolute', top: '-0.35rem', right: '-0.5rem' }}
                />
              </div>
            )}
            key="result-files"
          >
            <ConnectionUserFiles
              files={resultFiles}
              onDownload={downloadFile}
              onDeleteSelected={noopDeleteFiles}
              canDelete={false}
              showSelectionColumn={false}
            />
          </TabPanel>
        ) : null}
        {showSubmittedTabs && !isEditableStatus ? (
          <TabPanel
            header={(
              <div style={{ position: 'relative', paddingRight: '1.25rem', display: 'inline-block' }}>
                <span>Энергосбыт</span>
                <Badge
                  value={sbitFiles.length}
                  severity="danger"
                  style={{ position: 'absolute', top: '-0.35rem', right: '-0.5rem' }}
                />
              </div>
            )}
            key="sbit-files"
          >
            <ConnectionUserFiles
              files={sbitFiles}
              onDownload={downloadFile}
              onDeleteSelected={noopDeleteFiles}
              canDelete={false}
              showSelectionColumn={false}
            />
          </TabPanel>
        ) : null}
      </TabView>
    </InfoPageLayout>
  );
};
